import {Icon} from '@wandb/components';
import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
import React, {useCallback, useContext, useState} from 'react';

import {
  AGENT_NAME,
  useAgentChatReadonly,
  useWBAgentController,
  WBAgentEntryPointTooltip,
} from '../../../aria';
import {useAskAriaPanel} from '../../../aria/externalBindings/useAskAriaPanel';
import {useSelector} from '../../../state/hooks';
import {PanelBankUpdaterContext} from '../../../state/panelbank/context';
import {RunQueryContext} from '../../../state/runs/context';
import * as ViewHooks from '../../../state/views/hooks';
import * as PanelBankSectionConfigActions from '../../../state/views/panelBankSectionConfig/actions';
import type * as PanelBankSectionConfigTypes from '../../../state/views/panelBankSectionConfig/types';
import {DD_PRIVACY_ALLOW} from '../../../util/datadog/privacy';
import {useIsMobile} from '../../../util/panelbankFlow';
import {type ImageExportType} from '../../../util/panelExport';
import {isExportableAs} from '../../../util/panels';
import {type getPanelSpec} from '../../../util/panelTypes';
import {editablePanelDOMID} from '../../../util/report/export';
import {PanelViewSourceDropdownItem} from '../../PanelMarimo/PanelViewSourceDropdownItem';
import {usePanelsSharedRefsContext} from '../../Views/contexts/PanelsSharedRefsContext';
import {weaveQueryTablePanelToPart} from '../../WorkspaceBanners/evalTableMigration';
import {PanelDeleteMenuItem} from '../common/PanelDeleteMenuItem';
import {PanelDuplicateMenuItem} from '../common/PanelDuplicateMenuItem';
import {PanelOverflowMenu} from '../common/PanelOverflowMenu';
import {useSectionsForMovePanel} from '../hooks/useSectionsForMovePanel';
import {usePanelContext} from '../PanelContextProvider';
import {PanelExportMenu} from '../PanelExportModal/PanelExportMenu';
import {PanelExportModalContainer} from '../PanelExportModal/PanelExportModalContainer';
import {type ExportType} from '../types';
import {MovePanelSubmenu} from './MovePanelSubmenu';
import {
  PanelShareInReportMenuItem,
  PanelShareInReportModalContainer,
} from './PanelShareInReportMenuItem';
import {WorkspacePanelCopyLinkMenuItem} from './WorkspacePanelCopyLinkMenuItem';

type WorkspacePanelOverflowMenuProps = {
  defaultMoveToSectionRef?: PanelBankSectionConfigTypes.Ref;
  panelSpec: ReturnType<typeof getPanelSpec>;
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isOpen: boolean;
};

export const WorkspacePanelOverflowMenu = ({
  defaultMoveToSectionRef,
  panelSpec,
  panelBankSectionConfigRef,
  setIsOpen,
  isOpen,
}: WorkspacePanelOverflowMenuProps) => {
  const isMobileLayout = useIsMobile();
  const runsContext = useContext(RunQueryContext);
  const {entityName, projectName, report} = runsContext;
  const {panelBankConfigRef, workspaceSettingsRef} =
    usePanelsSharedRefsContext();
  const {panel, panelRef, trackPanelControls, isReadOnly, isProjectReadOnly} =
    usePanelContext();
  const view = useSelector(state => state.views.views[panelRef.viewID]);
  const panelName =
    panelSpec.getTitleFromConfig?.(panel.config as any) ?? panel.viewType;
  const [exportType, setExportType] = useState<ExportType>();
  const [imageExportType, setImageExportType] = useState<ImageExportType>();
  const unsetExportType = useCallback(() => setExportType(undefined), []);

  const [reportID, setReportID] = useState<undefined | string>();
  const [reportName, setReportName] = useState('');
  const {isAvailable: isWBAgentChatAvailable} = useWBAgentController();
  const {askAriaPanel, isCapturing} = useAskAriaPanel();
  const isChatReadonly = useAgentChatReadonly();

  const sectionsForMovePanel = useSectionsForMovePanel();
  const isMovePanelAllowed = report == null && sectionsForMovePanel.length > 0;

  const duplicatePanel = ViewHooks.useViewAction(
    panelBankSectionConfigRef,
    PanelBankSectionConfigActions.duplicatePanel
  );

  const deletePanel = ViewHooks.useViewAction(
    panelBankSectionConfigRef,
    PanelBankSectionConfigActions.deletePanel
  );
  const onRemovePanel = useCallback(() => {
    deletePanel(panelRef, panelBankConfigRef, workspaceSettingsRef);
    trackPanelControls({action: 'Panel deleted'});
  }, [
    deletePanel,
    panelRef,
    panelBankConfigRef,
    workspaceSettingsRef,
    trackPanelControls,
  ]);

  const onDuplicatePanel = useCallback(() => {
    duplicatePanel(panelRef, panelBankConfigRef);
    trackPanelControls({action: 'Panel duplicated'});
  }, [duplicatePanel, panelRef, panelBankConfigRef, trackPanelControls]);

  const {setExportingPanelRefID} = useContext(PanelBankUpdaterContext);

  if (isMobileLayout) {
    return null;
  }
  let weavePart = null;
  if (entityName && projectName) {
    weavePart = weaveQueryTablePanelToPart(panel, entityName, projectName);
  }

  return (
    <>
      <PanelExportModalContainer
        exportType={exportType}
        imageExportType={imageExportType}
        panelType={panelSpec.type}
        unsetExportType={unsetExportType}
      />
      {reportID && (
        <PanelShareInReportModalContainer
          reportID={reportID}
          reportName={reportName}
          setReportID={setReportID}
        />
      )}
      <PanelOverflowMenu isMenuOpen={isOpen} setIsMenuOpen={setIsOpen}>
        <DropdownMenu.Content
          // z-index of 6 needed here to exceed resizeable workspace panels,
          // which go to z-5 when they are hovered. Previously this was z-2 to
          // work with reports, somehow. z-6 seems safe.
          className="z-[6]"
          align="start">
          {panelSpec.type === 'Marimo' && (
            <PanelViewSourceDropdownItem panelRef={panelRef} />
          )}
          {isWBAgentChatAvailable && entityName && projectName && (
            <DropdownMenu.Item
              disabled={isChatReadonly || isCapturing}
              aria-busy={isCapturing}
              onClick={() => {
                void askAriaPanel(
                  {
                    type: 'wandb-workspace-panel',
                    entityName,
                    projectName,
                    // Use the persisted server ID, rather than the normalization ID.
                    viewId: view?.id,
                    viewType: view?.type,
                    panelId: panel.__id__,
                    panelType: panel.viewType,
                    title: panelName,
                    queryPanelTableKey: weavePart?.queryPanelTableKey,
                    queryPanelTableKeyType: weavePart?.queryPanelTableKeyType,
                  },
                  isExportableAs(panelSpec, 'image'),
                  editablePanelDOMID(panel)
                );
              }}>
              <Icon icon="lightboard-solid" />
              <span className="grow">
                <WBAgentEntryPointTooltip
                  label="Add this panel to an ARIA chat"
                  side="right">
                  <span>
                    {isCapturing ? 'Attaching…' : `Ask ${AGENT_NAME}`}
                  </span>
                </WBAgentEntryPointTooltip>
              </span>
              <div
                style={{
                  color: isChatReadonly ? 'inherit' : 'var(--tertiary-text)',
                }}>
                {isChatReadonly ? `${AGENT_NAME} is read-only` : undefined}
              </div>
            </DropdownMenu.Item>
          )}
          {!isProjectReadOnly && (
            <DropdownMenu.Item
              data-dd-action-name="Add panel to report"
              data-dd-privacy={DD_PRIVACY_ALLOW}
              data-test="add-to-report-button"
              onClick={() => {
                trackPanelControls({
                  action: 'Add to report clicked',
                });
                setExportingPanelRefID(panelRef.id);
              }}>
              <Icon icon="add-to-report" /> Add panel to report
            </DropdownMenu.Item>
          )}
          <WorkspacePanelCopyLinkMenuItem
            panelBankSectionConfigRef={panelBankSectionConfigRef}
          />
          <PanelExportMenu
            panelSpec={panelSpec}
            setExportType={setExportType}
            setImageExportType={setImageExportType}
          />
          {/* Share panel */}
          {!isReadOnly && !isProjectReadOnly && !isMobileLayout && (
            <PanelShareInReportMenuItem
              setReportID={setReportID}
              setReportName={setReportName}
            />
          )}
          <DropdownMenu.Separator />
          <PanelDuplicateMenuItem onSelect={onDuplicatePanel} />
          {isMovePanelAllowed &&
            panelBankConfigRef &&
            panelBankSectionConfigRef && (
              <MovePanelSubmenu
                panelBankConfigRef={panelBankConfigRef}
                panelBankSectionConfigRef={panelBankSectionConfigRef}
                eligibleSections={sectionsForMovePanel}
                defaultMoveToSectionRef={defaultMoveToSectionRef}
              />
            )}
          <DropdownMenu.Separator />
          <PanelDeleteMenuItem onSelect={onRemovePanel} />
        </DropdownMenu.Content>
      </PanelOverflowMenu>
    </>
  );
};
